import {
  AutopilotTaskDto,
  TaskCategoryTaskType,
  TaskCategoryWorkflowType,
  TaskCategoryStage,
  useInvalidateUseGetAutopilotTasksForCampaign,
} from '@urbanx/agx.activities.client'
import {
  CampaignActionRequest,
  CampaignActionRequestCampaignAction,
  usePerformCampaignAction,
  UploadedDocumentInfoDocumentType,
  UploadedDocumentInfoSigningMethod,
  useStaffGetDocumentsForCampaign,
  CampaignDetailModelStage,
} from '@urbanx/agx.campaigns.client'
import { AuthContext } from '@urbanx/agx.auth.client'
import {
  AgxButton,
  AgxModal,
  AgxSpacer,
  AgxYesNoQuestion,
  Icon_AlertCircleOutline,
  Icon_FeaturedIcon,
  cleanFullAddress,
  YesNo,
} from '@urbanx/agx.ui-components2'
import { MenuTitles } from 'constants/menu'
import { useSelectedAgency, useSelectedCampaign } from 'hooks'
import { ContentLayout, PageLayout } from 'layout'
import { useCallback, useContext, useMemo, useState } from 'react'
import AgreementCompliance from './AgreementCompliance/AgreementCompliance'
import Tasks, { TaskTabs } from 'components/tasks/Tasks'
import TaskDueDateBadge from 'components/tasks/TaskDueDateBadge'
import { getStepsCompletionInfo } from 'utils/tasks'

const CampaignTasks = () => {
  const { userAccount } = useContext(AuthContext)
  const { agency } = useSelectedAgency()
  const { campaign } = useSelectedCampaign()

  const [hasFundInTrust, setHasFundInTrust] = useState<boolean | undefined>()

  const [showWithdrawConfirmation, setShowWithdrawConfirmation] = useState(false)
  const [allTasks, setAllTasks] = useState<AutopilotTaskDto[]>([])

  const invalidateTasks = useInvalidateUseGetAutopilotTasksForCampaign()

  const { data: documentGroups, isPending: documentsPending } =
    useStaffGetDocumentsForCampaign(
      campaign?.campaignId || '',
      !!campaign?.campaignId
    )

  const agencyAgreementSignedByAgent = useMemo(() => {
    return documentGroups
      ?.flatMap((group) => group.documents ?? [])
      .find((doc) => doc.documentType === UploadedDocumentInfoDocumentType.AgencyAgreement &&
        doc.signingMethod === UploadedDocumentInfoSigningMethod.Agent
      )
  }, [documentGroups])

  const refreshCampaignTasks = useCallback(async () => {
    if (!campaign?.campaignId) return

    // The campaign action completes before downstream task rebuilds always
    // settle, so retry shortly to pick up the refreshed task set in all tabs.
    for (const delayMs of [1000]) {
      await new Promise((resolve) => setTimeout(resolve, delayMs))
      await invalidateTasks(campaign.campaignId)
    }
  }, [campaign?.campaignId, invalidateTasks])


  const { performCampaignAction } = usePerformCampaignAction({
    onSuccess: () => refreshCampaignTasks(),
    onError: () => { },
  })

  const onWithdrawListing = () => setShowWithdrawConfirmation(true)
  const onConfirmWithdraw = () => {
    if (hasFundInTrust === undefined) {
      return
    }

    const request: CampaignActionRequest = {
      campaignId: campaign?.campaignId,
      campaignAction: CampaignActionRequestCampaignAction.Withdraw,
      hasFundInTrust: hasFundInTrust,
    }
    performCampaignAction(request)
    setShowWithdrawConfirmation(false)
  }

  const withdrawnTask = useMemo(() => {
    return allTasks.find((t) => t.category?.taskType === TaskCategoryTaskType.Withdrawn)
  }, [allTasks])

  const complianceTasks = useMemo(() => {
    return allTasks.filter((t) => !t.isLocked &&
      t.category?.workflowType === TaskCategoryWorkflowType.Campaign &&
      t.category?.stage === TaskCategoryStage.AgreementCompliance
    )
  }, [allTasks])

  const { allStepsCompleted: allComplianceStepsCompleted, latestCompletedDate } =
    useMemo(() => getStepsCompletionInfo(complianceTasks), [complianceTasks])

  const handleTasksChange = useCallback((tasks: AutopilotTaskDto[]) => {
    setAllTasks(tasks)
  }, [])

  // Filter out AgreementCompliance tasks from main Tasks list (they're rendered separately)
  const taskFilter = useCallback(
    (task: AutopilotTaskDto) => task.category?.stage !== TaskCategoryStage.AgreementCompliance,
    []
  )

  const renderAdditionalContent = useCallback(
    ({
      selectedTab,
      selectedWorkflowStage,
      allTasksCompleted,
      isViewingCompletedStage,
    }: {
      selectedTab: TaskTabs
      selectedWorkflowStage?: TaskCategoryStage
      allTasksCompleted: boolean
      isViewingCompletedStage: boolean
    }) => {
      const shouldShowCompliance = selectedWorkflowStage === TaskCategoryStage.AgreementCompliance ||
        (!allComplianceStepsCompleted && !documentsPending &&
          selectedTab === TaskTabs.Tasks && agencyAgreementSignedByAgent &&
          complianceTasks.length > 0)

      // Don't show compliance when viewing a different completed stage
      if (
        isViewingCompletedStage &&
        selectedWorkflowStage !== TaskCategoryStage.AgreementCompliance
      )
        return null
      // Don't show when all completed and not viewing a specific stage
      if (allTasksCompleted && !isViewingCompletedStage) return null
      if (!shouldShowCompliance) return null

      const dueDate = complianceTasks
        .flatMap((task) => {
          const dateValue = allComplianceStepsCompleted
            ? latestCompletedDate
            : task.dueUtc

          return dateValue ? [new Date(dateValue)] : []
        })
        .sort((a, b) => a.getTime() - b.getTime())[0] ?? ''

      return (
        <AgreementCompliance
          badge={
            <TaskDueDateBadge
              dueDate={dueDate}
              isCompleted={allComplianceStepsCompleted}
              completedDate={latestCompletedDate}
            />
          }
          tasks={complianceTasks}
        />
      )
    },
    [
      allComplianceStepsCompleted,
      documentsPending,
      agencyAgreementSignedByAgent,
      complianceTasks,
      latestCompletedDate,
    ]
  )

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={agency?.name || ''}
      currentPageTitle={`Campaigns / ${cleanFullAddress(campaign?.address)}`}
      isCampaignsSection={true}
    >
      <ContentLayout
        hasSecondaryMenu={false}
        secondaryActiveMenu={MenuTitles.CAMPAIGN_TASKS}
        isCampaigns={true}
        secondaryActions={
          <AgxButton
            id={'btnWithdrawListing'}
            text={'Withdraw Listing'}
            variant={'naked'}
            danger
            onClick={onWithdrawListing}
            disabled={
              // ToDo: Active contract blockers 
              campaign?.stage == CampaignDetailModelStage.ListingWithdrawn ||
              (withdrawnTask && !withdrawnTask.isLocked)
            }
          />
        }
      >
        <Tasks
          campaignId={campaign?.campaignId ?? ''}
          primaryWorkflowType={TaskCategoryWorkflowType.Campaign}
          taskFilter={taskFilter}
          onTasksChange={handleTasksChange}
          renderAdditionalContent={renderAdditionalContent}
        />

        <AgxModal
          title={'Mark as Withdrawn?'}
          titleIcon={
            <Icon_FeaturedIcon
              icon={<Icon_AlertCircleOutline type="Danger" />}
              color="var(--text-error)"
              backgroundColor="var(--bg-error-light)"
            />
          }
          type="single"
          variant={'withDivider'}
          actionButtonContainerStyle={'withoutDivider'}
          actionButtons={
            <>
              <AgxButton
                id={'btnCancel'}
                text={'Cancel'}
                variant={'hollow'}
                size="large"
                onClick={() => {
                  setShowWithdrawConfirmation(false)
                }}
                stretch
              />
              <AgxButton
                id={'btnConfirm'}
                text={'Confirm'}
                variant={'primary'}
                danger
                size="large"
                onClick={onConfirmWithdraw}
                stretch
                disabled={hasFundInTrust === undefined}
              />
            </>
          }

          content={
            <AgxSpacer
              direction="vertical"
              fill
              padding={{ top: 'md', bottom: 'xl', left: 'xl', right: 'xl' }}
            >
              <AgxYesNoQuestion
                id={'fundsInTrustQuestion'}
                onValueChanged={({ value }: { id: string; value: string }) => {
                  setHasFundInTrust(value === YesNo.Yes)
                }}
                defaultValue={
                  hasFundInTrust === undefined
                    ? undefined
                    : hasFundInTrust
                      ? YesNo.Yes
                      : YesNo.No
                }
                label={'Are there any funds in trust?'}
                yesLabel={'Yes'}
                noLabel={'No'}
                reverseLabelOrder
                required
              />
            </AgxSpacer>
          }
          isOpen={showWithdrawConfirmation}
          setIsDialogOpen={setShowWithdrawConfirmation}
        />
      </ContentLayout>
    </PageLayout>
  )
}

export default CampaignTasks
